import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { DropdownMenu, TextDisplay, Tooltip, Row, Col } from '@paypalcorp/pp-react';
import styles from './ControlPanel.module.less';
import IntegrationPageSectionContext from '../StateManagement/IntegrationPageSectionContext';
import TestPanelContext from '../StateManagement/TestPanelContext';
import { trackFieldChange, trackFieldFocus, trackFieldClosed } from '../../../lib/fpti';
import { isUserLoggedInSelector } from '../../../store/App/selectors';

export default function ControlPanel() {
  const {
    code: {
      language: { frontend = '', backend = '' },
      updateCodeSelection
    },
    SdkConfigurator: {
      updateSdkSettings,
      sdkSettings: { clientId }
    },
    integrationType: type,
    apps,
    pageMetaDataConfig
  } = useContext(IntegrationPageSectionContext);

  const { resetTestPanel } = useContext(TestPanelContext);
  const isUserLoggedIn = useSelector(isUserLoggedInSelector);
  const { product } = useParams();
  const [searchParams] = useSearchParams();

  const langBarClassNames = classNames(styles.language_sticky_bar, {
    [styles.language_sticky_bar_logged_out]: !isUserLoggedIn,
    [styles.language_sticky_bar_logged_in]: isUserLoggedIn
  });

  const defaultConfig = (currentProduct) => {
    switch (currentProduct) {
      case 'fastlane':
        return {
          frontend: [{ value: 'html', label: 'HTML' }],
          backend: [{ value: 'javascript', label: 'Node.js' }]
        };
      case 'standard':
      case 'advanced':
        return {
          frontend: [
            { value: 'html', label: 'HTML' },
            { value: 'react', label: 'React.js' }
          ],
          backend: [{ value: 'javascript', label: 'Node.js' }]
        };
      default:
        return {
          frontend: [{ value: 'html', label: 'HTML' }],
          backend: [{ value: 'javascript', label: 'Node.js' }]
        };
    }
  };

  const appsDropdownOptions = apps.map((app) => {
    return {
      value: app.clientId,
      label: app.name
    };
  });

  // Saving this for when the SDK team adds solution to cleanup store on client-side.
  /*
  const fetchClientToken = async (appClientId) => {
    try {
      const tokenRequest = await fetch('/studio/api/v1/apps/sandbox-token', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          clientId: appClientId
        })
      });
      const { response, message } = await tokenRequest.json();
      if (tokenRequest.status >= 300) {
        clientLogger.cal.error('SERVICE_ERROR', {
          message: `error with fastlane client-side fetchClientToken. status: ${tokenRequest.status}, message: ${message}`
        });
        return { error: message };
      }
      return { clientAccessToken: response };
    } catch (error) {
      clientLogger.cal.warn('NETWORK_ERROR', {
        message: `error with fastlane client-side fetchClientToken. status: ${error.message}`
      });
      return { error };
    }
  };
  */

  const handleAppsDropdownChange = async (e) => {
    if (clientId === e.target.value) return;
    trackFieldChange('App', e.target.value);
    if (product === 'fastlane') {
      // Saving this for when the SDK team adds solution to cleanup store on client-side.
      /* setFastlaneContext(true, 'isLoading');
      const { clientAccessToken, error } = await fetchClientToken(e.target.value);
      if (!error && clientAccessToken) {
        updateSdkSettings(e.target.value, 'clientId');
        updateSdkSettings(clientAccessToken, 'clientAccessToken');
      } else {
        navigate('/error');
      }
      */
      const name = apps[e.target.selectedIndex]?.name || 'Default Application';

      // Clone the current search params and add or modify the parameters
      const newParams = new URLSearchParams(searchParams);
      newParams.set('app', name.replaceAll(' ', '_')); // Add or update the parameter
      newParams.set('frontend', frontend);
      newParams.set('backend', backend);
      newParams.set('type', type);
      window.open(
        `/studio/checkout/fastlane/integrate?${newParams.toString()}`,
        '_self',
        'popup=false'
      );
    } else {
      updateSdkSettings(e.target.value, 'clientId');
      resetTestPanel();
    }
  };

  return (
    <Row noGutters className={langBarClassNames} id="language-selector">
      <Col xs="auto" className={styles.language_field}>
        <DropdownMenu
          className={styles.language_dropdown}
          label="Frontend"
          options={pageMetaDataConfig?.languages?.frontend || defaultConfig(product).frontend}
          onClick={() => trackFieldFocus('Frontend')}
          onChange={(e) => {
            updateCodeSelection(e.target.value, 'frontend');
            trackFieldChange('Frontend', e.target.value);
          }}
          onClose={() => trackFieldClosed('Frontend')}
          value={frontend}
        />
      </Col>
      <Col xs="auto" className={styles.language_field}>
        <DropdownMenu
          className={styles.language_dropdown}
          label="Backend"
          options={pageMetaDataConfig?.languages?.backend || defaultConfig(product).backend}
          onClick={() => trackFieldFocus('Backend')}
          onChange={(e) => {
            updateCodeSelection(e.target.value, 'backend');
            trackFieldChange('Backend', e.target.value);
          }}
          onClose={() => trackFieldClosed('Backend')}
          value={backend}
        />
      </Col>
      {apps.length > 0 && (
        <Col xs="auto" className={`${styles.language_field} ${styles.credentials_container}`}>
          <p>Credentials</p>
          <Tooltip name="credentials" description="Sandbox application credentials" />
          <DropdownMenu
            className={styles.language_dropdown}
            label="App"
            options={appsDropdownOptions}
            onClick={() => trackFieldFocus('App')}
            onChange={handleAppsDropdownChange}
            onClose={() => trackFieldClosed('App')}
            value={clientId}
          />
          <TextDisplay
            className="credentials-client-input"
            valueClassName="credentials-client-id"
            name="basic"
            isLabelHidden={!clientId}
            label="Client ID"
            value={clientId}
          />
        </Col>
      )}
    </Row>
  );
}
